/**
 *  @class
 *  @function GridDensity
 *  The collection and search grids' column count, chosen by the shopper:
 *  2, 3 or 4 on desktop and 1 or 2 on phones (facets-bar.liquid). The
 *  choice is kept on the device and set on .collection-container as
 *  data-grid-desktop and data-grid-mobile, which product-grid.scss lays
 *  the grid out by. The container sits outside what filtering redraws, so
 *  the choice outlives it; theme.liquid sets the stored one on it as the
 *  page is parsed, before the grid is first drawn. The toggle is redrawn
 *  with the filters, and follows the container whenever it connects.
 *
 *  In the theme editor the grid always opens on the section's own columns,
 *  so a change to them shows, and a click there is not stored.
 */
if (!customElements.get('grid-density')) {
  const storageKey = 'theme-grid-density';
  const choices = {
    desktop: ['2', '3', '4'],
    mobile: ['1', '2']
  };

  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const readChoice = () => {
    if (designMode) return {};
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey));
      return stored && typeof stored === 'object' ? stored : {};
    } catch (error) {
      return {};
    }
  };

  const storeChoice = (device, columns) => {
    if (designMode) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(Object.assign(readChoice(), { [device]: columns })));
    } catch (error) {
      // Private mode or storage blocked: the choice lasts for this page.
    }
  };

  // The width a card is drawn at, for the sizes of its images: an eager
  // image picks its candidate from it, and it is the fallback of a lazy
  // one (sizes="auto, ...") in browsers that can't measure. The server
  // wrote the section's starting columns.
  const updateImages = (container) => {
    const sizes = `(min-width: 768px) calc(100vw / ${container.dataset.gridDesktop}), ${container.dataset.gridMobile === '1' ? '100vw' : '50vw'}`;
    container.querySelectorAll('#product-grid product-card img[sizes]').forEach((image) => {
      const value = image.getAttribute('sizes').startsWith('auto') ? `auto, ${sizes}` : sizes;
      if (image.getAttribute('sizes') !== value) image.setAttribute('sizes', value);
    });
  };

  class GridDensity extends HTMLElement {
    connectedCallback() {
      this.container = this.closest('.collection-container[data-grid-density]');
      if (!this.container) return;
      // The stored choice, for a container theme.liquid did not see: one
      // the theme editor redrew.
      const stored = readChoice();
      Object.keys(choices).forEach((device) => {
        const columns = String(stored[device]);
        if (choices[device].includes(columns)) this.container.setAttribute(`data-grid-${device}`, columns);
      });
      this.update();
      this.addEventListener('click', this.onClick);
    }

    onClick(event) {
      const button = event.target.closest('button[data-columns]');
      const group = button?.closest('[data-grid-device]');
      if (!group || !this.container || button.getAttribute('aria-pressed') === 'true') return;
      const device = group.dataset.gridDevice,
        columns = button.dataset.columns;
      storeChoice(device, columns);
      const apply = () => {
        this.container.setAttribute(`data-grid-${device}`, columns);
        this.update();
      };
      if (typeof withViewTransition === 'function') {
        withViewTransition(apply);
      } else {
        apply();
      }
    }

    // The buttons and the card images follow the container.
    update() {
      this.querySelectorAll('[data-grid-device]').forEach((group) => {
        const columns = this.container.getAttribute(`data-grid-${group.dataset.gridDevice}`);
        group.querySelectorAll('button[data-columns]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.columns === columns));
        });
      });
      updateImages(this.container);
    }
  }
  customElements.define('grid-density', GridDensity);

  // Load more and infinite scroll add their cards once they are in the grid.
  document.addEventListener('pagination:page-changed', () => {
    document.querySelectorAll('.collection-container[data-grid-density][data-grid-desktop]').forEach(updateImages);
  });
}
